import {Component, HostListener, OnDestroy, OnInit} from '@angular/core';
import {AppBreadcrumbService} from './app.breadcrumb.service';
import {Subscription} from 'rxjs';
import {MenuItem} from 'primeng/api';
import {TokenDataProviderService} from '../../core/services/token-data-provider.service';
import {CurrentUserViewModel} from '../../core/models/current-user.view-model';
import {Location} from '@angular/common';
import {ActivatedRoute, NavigationEnd, NavigationStart, Router} from '@angular/router';
import {filter} from 'rxjs/operators';
import {TranslateService} from '@ngx-translate/core';
import {ActionsService} from '../footer/actions.service';
import {ViewTypeEnum} from '../../core/enums/view-type.enum';

export let browserRefresh = false;


@Component({
  selector: 'app-breadcrumb',
  templateUrl: './app.breadcrumb.component.html',
  styleUrls: ['./app.breadcrumb.component.scss']
})
export class AppBreadcrumbComponent implements OnInit, OnDestroy {
  static readonly ROUTE_DATA_BREADCRUMB = 'breadcrumb';
  public readonly ViewTypeEnum = ViewTypeEnum;
  private readonly mobileBreakpoint = 768;

  subscriptions: Subscription[] = [];

  items: MenuItem[];

  home: MenuItem;

  public currentUser: CurrentUserViewModel;
  public isFocused: boolean = false;
  public isLoggedIn: boolean = false;
  public isMobileViewport: boolean = false;

  constructor(
    public breadcrumbService: AppBreadcrumbService,
    public actionsService: ActionsService,
    public tdp: TokenDataProviderService,
    private location: Location,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private translate: TranslateService,
  ) {
    this.subscriptions.push(breadcrumbService.itemsHandler.subscribe(response => {
      this.items = response;
    }));

    this.subscriptions.push(this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event) => {
          this.publishItems(this.createBreadcrumbs(this.activatedRoute.root));
        }
      ));

    this.translate.onLangChange.subscribe(() => {
      this.publishItems(this.createBreadcrumbs(this.activatedRoute.root));
    });

    this.home = {
      icon: '',
      routerLink: '/'
    };
  }

  ngOnInit(): void {
    this.updateViewportState();
    this.tdp.currentUser.subscribe((currentUser: CurrentUserViewModel) => {
      this.currentUser = currentUser;
      this.isLoggedIn = this.tdp.isLoggedIn();
    });
    this.isLoggedIn = this.tdp.isLoggedIn();
    this.publishItems(this.createBreadcrumbs(this.activatedRoute.root));
  }

  ngOnDestroy() {
      this.subscriptions.forEach(s => s.unsubscribe());
  }

  public logout(): void {
    this.tdp.logout();
  }

  public goBack(): void {
    this.location.back();
  }

  private createBreadcrumbs(route: ActivatedRoute, url: string = '', breadcrumbs: MenuItem[] = []): MenuItem[] {
    const children: ActivatedRoute[] = route.children;

    if (children.length === 0) {
      return breadcrumbs;
    }

    for (const child of children) {
      const routeURL: string = child.snapshot.url.map(segment => segment.path).join('/');
      if (routeURL !== '') {
        url += `/${routeURL}`;
      }

      let label = child.snapshot.data[AppBreadcrumbComponent.ROUTE_DATA_BREADCRUMB];

      if (label !== null && label !== undefined) {

        label = this.translate.instant(label);
        breadcrumbs.push({label, url});
      }

      return this.createBreadcrumbs(child, url, breadcrumbs);
    }
  }

  private publishItems(items: MenuItem[]): void {
    this.items = items;
    this.breadcrumbService.setItems(items);
  }

  public appendQuery(): void {
    this.router.navigate([], {queryParams: {q: this.breadcrumbService.query}});
  }

  public resetQuery(): void {
    this.breadcrumbService.query = '';
    sessionStorage.removeItem('queryParams');
    sessionStorage.removeItem('marketplace.search.address');
    sessionStorage.removeItem('event.search.address');
    sessionStorage.removeItem('event.search.includeArchived');
    this.router.navigate([], {queryParams: {}, replaceUrl: true});
    this.breadcrumbService.emitQuery('');
  }

  public handleProfileClick(event: Event): void {
    event.preventDefault();
    if (!this.isLoggedIn || !this.currentUser?.slug) {
      this.router.navigate(['/login']);
      return;
    }
    this.router.navigate(['/', this.currentUser.slug, 'profile']);
  }

  public handleGarageClick(event: Event): void {
    event.preventDefault();
    if (!this.isLoggedIn || !this.currentUser?.slug) {
      this.router.navigate(['/login']);
      return;
    }
    this.router.navigate(['/', this.currentUser.slug, 'garage']);
  }

  onFocus(): void {
    this.isFocused = true;
  }

  onBlur(): void {
    this.isFocused = false;
  }

  public get displayItems(): MenuItem[] {
    if (!this.isVehicleCatalogRoute() || !this.isMobileViewport) {
      return this.items;
    }

    return this.items?.slice(0, 1) ?? [];
  }

  @HostListener('window:resize')
  public onWindowResize(): void {
    this.updateViewportState();
  }

  private updateViewportState(): void {
    this.isMobileViewport = typeof window !== 'undefined' && window.innerWidth <= this.mobileBreakpoint;
  }

  private isVehicleCatalogRoute(): boolean {
    return this.router.url.split('?')[0].startsWith('/vehicles/');
  }
}
